import { formatDateTime, formatMoney, PageEmpty, PageError, PageLoading } from "@majitap/ui";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { ageingQuery } from "../../invoices";
import { RouterLink } from "../../router-link";
import { BUCKETS } from "../shared/finance";
import styles from "./invoices.module.css";

/** The same report as a file, for the accounts team. */
export const AGEING_CSV = "/v1/admin/billing/ageing?format=csv";

/**
 * What every payer owes, by how overdue (`GET /billing/ageing`). Each name opens the payer's
 * Credit tab, with their statement. The amounts are the API's; nothing is added up here.
 */
export function AgeingView(): ReactNode {
  const format = useCompanyFormat();
  const ageing = useQuery(ageingQuery);
  if (ageing.status === "pending") return <PageLoading label="Loading ageing" />;
  if (ageing.status === "error")
    return <PageError message={messageFor(errorOf(ageing.error))} onRetry={() => ageing.refetch()} />;
  const a = ageing.data;
  const money = (v: string) => formatMoney(v, { ...format, currency: a.currency });
  if (a.rows.length === 0)
    return <PageEmpty title="Nobody owes anything" description="Payers with a balance or an open invoice show here." />;

  return (
    <div className={styles.panel}>
      <div className={styles.numbers}>
        {BUCKETS.map(([key, label]) => (
          <div key={key} className={styles.figure}>
            <span className={styles.muted}>{label}</span>
            <strong>{money(a.totals[key])}</strong>
          </div>
        ))}
        <div className={styles.figure}>
          <span className={styles.muted}>Not invoiced</span>
          <strong>{money(a.totals.not_invoiced)}</strong>
        </div>
        <div className={styles.figure}>
          <span className={styles.muted}>Owed in all</span>
          <strong>{money(a.totals.balance)}</strong>
        </div>
      </div>
      <a className={styles.link} href={AGEING_CSV} download>
        Download (CSV)
      </a>
      <SortableDataTable
        caption="Ageing by payer"
        rowKey={(r) => `${r.payer.type}-${r.payer.id}`}
        rows={a.rows}
        itemName={{ one: "payer", other: "payers" }}
        columns={[
          {
            key: "payer",
            label: "Payer",
            sortable: false,
            render: (_v, r) => (
              <RouterLink
                href={`/${r.payer.type === "business" ? "businesses" : "customers"}/${r.payer.id}?tab=credit`}
                className={styles.link}
              >
                {r.payer.name ?? "Unnamed"}
              </RouterLink>
            ),
          },
          ...BUCKETS.map(([key, label]) => ({
            key,
            label,
            numeric: true,
            sortable: false,
            render: (_v: unknown, r: (typeof a.rows)[number]) => money(r[key]),
          })),
          {
            key: "invoiced_due",
            label: "On invoices",
            numeric: true,
            sortable: false,
            render: (_v, r) => money(r.invoiced_due),
          },
          {
            key: "not_invoiced",
            label: "Not invoiced",
            numeric: true,
            sortable: false,
            render: (_v, r) => money(r.not_invoiced),
          },
          {
            key: "balance",
            label: "Balance",
            numeric: true,
            sortable: false,
            render: (_v, r) => <span className={styles.strong}>{money(r.balance)}</span>,
          },
        ]}
      />
      <p className={styles.muted}>
        As of {formatDateTime(a.as_of, format)}. Not invoiced is delivered or charged but not on an invoice yet, less
        credit not set against one. Buckets plus not invoiced make the balance.
      </p>
    </div>
  );
}
